import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Trophy, ArrowClockwise, FilePdf, EnvelopeSimple, Buildings, Crown,
  SlackLogo, Eye, X, Code, Copy, Check, PencilSimple, CalendarSlash,
  CalendarCheck, Plus, Trash, ChartBar, CaretDown, CaretRight, Warning,
  ArrowUUpLeft, DownloadSimple,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * AdminSharingLeaderboardPage — admin-only ranking of fleets by
 * rate-card share activity. Turns individual milestones into visible
 * peer competition.
 *
 * Windows: all-time (default) · 90-day · 30-day.
 * Ranking key: downloads + successful emails.
 */
export default function AdminSharingLeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [window, setWindow] = useState("all");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [slackTestFiring, setSlackTestFiring] = useState(false);
  const [slackPreviewOpen, setSlackPreviewOpen] = useState(false);
  const [slackPreviewLoading, setSlackPreviewLoading] = useState(false);
  const [slackPreview, setSlackPreview] = useState(null);
  const [previewJsonCopied, setPreviewJsonCopied] = useState(false);
  const [mockEditorOpen, setMockEditorOpen] = useState(false);
  const [mockNames, setMockNames] = useState(["", "", ""]);
  const [skipSettings, setSkipSettings] = useState(null);
  const [skipSaving, setSkipSaving] = useState(false);
  const [skipHistory, setSkipHistory] = useState([]);
  const [skipReason, setSkipReason] = useState("");
  const [skipReasonOpen, setSkipReasonOpen] = useState(false);
  // Custom preset chips — admin-registered skip reasons that persist
  // in the DB and are shared across the team. Loaded on mount alongside
  // skipSettings so the row renders in one paint.
  const [customPresets, setCustomPresets] = useState([]);
  const [presetAdderOpen, setPresetAdderOpen] = useState(false);
  const [newPresetLabel, setNewPresetLabel] = useState("");
  const [presetSaving, setPresetSaving] = useState(false);
  const BUILT_IN_PRESETS = ["Holiday", "Testing", "Migration", "Q-close"];
  // Analytics panel — collapsed by default so it doesn't push the
  // skip toggle below the fold. Ops open it when they want to prune
  // stale chips or study which reasons trend.
  const [presetAnalyticsOpen, setPresetAnalyticsOpen] = useState(false);
  // Trend data keyed by label_lower — 30-day pick buckets. Loaded
  // lazily the first time the analytics panel opens so idle admins
  // don't pay for the aggregate. Refreshed after each pick fires so
  // the sparkline animates in real time.
  const [presetTrends, setPresetTrends] = useState({});
  const [presetTrendsLoading, setPresetTrendsLoading] = useState(false);
  // Timespan for the sparkline — 7 / 30 / 90 days. Persisted so ops
  // sees the same zoom level next time they open the analytics
  // panel. Clamped to the three allowed values.
  const SPARKLINE_TIMESPANS = [7, 30, 90];
  const [sparklineDays, setSparklineDays] = useState(() => {
    try {
      const raw = localStorage.getItem("adminSharingLeaderboardSparklineDays");
      const n = parseInt(raw, 10);
      return SPARKLINE_TIMESPANS.includes(n) ? n : 30;
    } catch {
      return 30;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(
        "adminSharingLeaderboardSparklineDays", String(sparklineDays),
      );
    } catch {
      // Silent — degrade gracefully.
    }
  }, [sparklineDays]);

  const loadPresetTrends = async (daysOverride) => {
    setPresetTrendsLoading(true);
    const days = daysOverride ?? sparklineDays;
    try {
      const { data } = await client.get(
        `/admin/leaderboard-digest/skip-reason-presets/trends?days=${days}`,
      );
      setPresetTrends(data.trends || {});
    } catch (e) {
      // Silent — sparklines are decorative; the counter is the truth.
    } finally {
      setPresetTrendsLoading(false);
    }
  };

  useEffect(() => {
    // First open of the analytics panel → load trends. Subsequent
    // opens re-use the cached data unless a pick fires (which
    // triggers a refresh via pickCustomPreset).
    if (presetAnalyticsOpen && Object.keys(presetTrends).length === 0
        && customPresets.length > 0 && !presetTrendsLoading) {
      loadPresetTrends();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetAnalyticsOpen, customPresets.length]);

  useEffect(() => {
    // When the timespan toggle changes while the panel is open,
    // re-fetch trends with the new `days` param. Skip if the panel
    // is closed so we don't fetch on background changes.
    if (presetAnalyticsOpen && customPresets.length > 0) {
      loadPresetTrends(sparklineDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sparklineDays]);

  const presetAnalytics = useMemo(() => {
    // Guard: without any custom chips, the whole panel stays hidden.
    if (!customPresets || customPresets.length === 0) {
      return { top5: [], stale: [], totalPicks: 0, hasData: false };
    }
    const now = Date.now();
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const totalPicks = customPresets.reduce(
      (sum, p) => sum + (p.pick_count || 0), 0,
    );
    // Top 5 by pick_count (already server-sorted, but be defensive).
    const top5 = [...customPresets]
      .filter((p) => (p.pick_count || 0) > 0)
      .sort((a, b) => (b.pick_count || 0) - (a.pick_count || 0))
      .slice(0, 5);
    // Stale: never picked AND created >30d ago, OR last_picked_at >30d ago.
    const stale = customPresets.filter((p) => {
      const picks = p.pick_count || 0;
      if (picks === 0) {
        const createdAt = Date.parse(p.created_at || "");
        if (Number.isNaN(createdAt)) return false;
        return now - createdAt > THIRTY_DAYS_MS;
      }
      const lastAt = Date.parse(p.last_picked_at || "");
      if (Number.isNaN(lastAt)) return false;
      return now - lastAt > THIRTY_DAYS_MS;
    });
    return { top5, stale, totalPicks, hasData: true };
  }, [customPresets]);

  const load = async (win) => {
    setLoading(true);
    try {
      const { data } = await client.get(
        `/admin/sharing-leaderboard?window=${win}&limit=25`,
      );
      setRows(data.fleets || []);
      setTotal(data.fleets_total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const slackTestFire = async () => {
    setSlackTestFiring(true);
    try {
      const { data } = await client.post(
        "/admin/leaderboard-digest/slack-test-fire",
      );
      toast.success(
        `Posted a synthetic digest to Slack (${data.iso_week}).`,
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Slack test-fire failed. Check the webhook URL.",
      );
    } finally {
      setSlackTestFiring(false);
    }
  };

  const openSlackPreview = async () => {
    setSlackPreviewOpen(true);
    setSlackPreviewLoading(true);
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/slack-test-fire/preview",
      );
      setSlackPreview(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load Slack preview",
      );
      setSlackPreviewOpen(false);
    } finally {
      setSlackPreviewLoading(false);
    }
  };

  const refreshSlackPreview = async () => {
    // Refetch inside the modal without closing/reopening — useful
    // when the mock digest generator changes (e.g., ISO week rolls
    // over at midnight UTC or ops updates a fake fleet name).
    setSlackPreviewLoading(true);
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/slack-test-fire/preview",
      );
      setSlackPreview(data);
      setPreviewJsonCopied(false);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to refresh preview",
      );
    } finally {
      setSlackPreviewLoading(false);
    }
  };

  const closeSlackPreview = () => {
    setSlackPreviewOpen(false);
    setSlackPreview(null);
    setPreviewJsonCopied(false);
  };

  const copyPreviewJson = async () => {
    if (!slackPreview?.blocks) return;
    const json = JSON.stringify(slackPreview.blocks, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setPreviewJsonCopied(true);
      toast.success("Block Kit JSON copied to clipboard");
      setTimeout(() => setPreviewJsonCopied(false), 2000);
    } catch (e) {
      toast.error(
        "Clipboard copy blocked. Select the JSON manually to copy.",
      );
    }
  };

  useEffect(() => { load(window); }, [window]);
  useEffect(() => { loadSkipSettings(); }, []);

  const loadSkipSettings = async () => {
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/skip-settings",
      );
      setSkipSettings(data);
    } catch (e) {
      // Silent — the toggle is a nice-to-have. Skip-load failure
      // shouldn't spam ops.
    }
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/skip-history?limit=10",
      );
      setSkipHistory(data.skips || []);
    } catch (e) {
      // Silent
    }
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/skip-reason-presets",
      );
      setCustomPresets(data.presets || []);
    } catch (e) {
      // Silent — custom presets are a nicety; built-ins still render.
    }
    // Also refresh archived list so ops sees the Archived section
    // count without navigating away and back.
    loadArchivedPresets();
  };

  const addCustomPreset = async () => {
    const label = (newPresetLabel || "").trim();
    if (!label) return;
    setPresetSaving(true);
    try {
      const { data } = await client.post(
        "/admin/leaderboard-digest/skip-reason-presets",
        { label },
      );
      if (data.already_present) {
        toast.info(`"${data.label}" was already a saved preset.`);
      } else {
        toast.success(`"${data.label}" added as a preset chip.`);
      }
      setNewPresetLabel("");
      setPresetAdderOpen(false);
      // Auto-select the newly-added preset so ops can click Confirm right away.
      setSkipReason(data.label);
      // Refresh list from server so `created_by`/`created_at` populate.
      const { data: list } = await client.get(
        "/admin/leaderboard-digest/skip-reason-presets",
      );
      setCustomPresets(list.presets || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to add preset",
      );
    } finally {
      setPresetSaving(false);
    }
  };

  const pickCustomPreset = async (label) => {
    // Set the reason input immediately so ops sees the click
    // register without waiting for the round-trip.
    setSkipReason(label);
    try {
      const { data } = await client.post(
        `/admin/leaderboard-digest/skip-reason-presets/${encodeURIComponent(label)}/pick`,
      );
      // Optimistically bump the counter in the local list — the API
      // returns the fresh count so we align state exactly.
      setCustomPresets((prev) => {
        const next = prev.map((p) =>
          (p.label_lower || p.label.toLowerCase()) === label.toLowerCase()
            ? { ...p, pick_count: data.pick_count, last_picked_at: data.last_picked_at }
            : p,
        );
        // Re-sort so the freshly-picked chip drifts left if it just
        // overtook a neighbour. Match server sort: pick_count DESC,
        // created_at DESC.
        next.sort((a, b) => {
          const pa = a.pick_count || 0;
          const pb = b.pick_count || 0;
          if (pa !== pb) return pb - pa;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        return next;
      });
      // If the analytics panel is open, refresh trends so the
      // sparkline shows the new pick right away. Non-blocking.
      if (presetAnalyticsOpen) {
        loadPresetTrends();
      }
    } catch (e) {
      // Silent — the counter is nice-to-have; the preset itself
      // still landed in the input above.
    }
  };

  const removeCustomPreset = async (label) => {
    // "Archive" now — soft-delete. Chip disappears from the live list
    // but can be restored from the Archived section. Retains audit
    // trail (created_by/at + archived_by/at + pick history).
    try {
      await client.post(
        `/admin/leaderboard-digest/skip-reason-presets/${encodeURIComponent(label)}/archive`,
      );
      toast.success(`"${label}" archived — restore from Archived if needed.`);
      setCustomPresets((prev) => prev.filter(
        (p) => (p.label_lower || p.label.toLowerCase())
          !== label.toLowerCase(),
      ));
      // If ops was previewing this preset, clear the input.
      if (skipReason === label) setSkipReason("");
      // Refresh archived list so the archived section shows the
      // newly-tucked chip.
      loadArchivedPresets();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to archive ${label}`,
      );
    }
  };

  const [archivedPresets, setArchivedPresets] = useState([]);
  const loadArchivedPresets = async () => {
    try {
      const { data } = await client.get(
        "/admin/leaderboard-digest/skip-reason-presets/archived",
      );
      setArchivedPresets(data.presets || []);
    } catch (e) {
      // Silent — archived list is a bonus panel.
    }
  };

  const unarchiveCustomPreset = async (label) => {
    try {
      await client.post(
        `/admin/leaderboard-digest/skip-reason-presets/${encodeURIComponent(label)}/unarchive`,
      );
      toast.success(`"${label}" restored — back on the chip row.`);
      // Remove from archived list, refresh the live list.
      setArchivedPresets((prev) => prev.filter(
        (p) => (p.label_lower || p.label.toLowerCase())
          !== label.toLowerCase(),
      ));
      loadSkipSettings();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to restore ${label}`,
      );
    }
  };

  // Bulk-archive every currently-stale chip in one call. Fires an
  // Undo toast (5s) so ops can reverse the whole batch — the Undo
  // action posts to bulk-unarchive with the same label list.
  const [bulkArchivingStale, setBulkArchivingStale] = useState(false);
  // Two-step confirm — first click reveals the confirm state, second
  // click fires the archive. Prevents accidental mass-archive on
  // large stale lists where one wrong tap could tuck away 15+ chips.
  const [bulkArchiveConfirming, setBulkArchiveConfirming] = useState(false);

  // Client-side CSV assembly — no new endpoint, just projects the
  // already-loaded analytics + trends into columns ops can paste
  // into a slide or share with a stakeholder.
  const exportAnalyticsCsv = () => {
    if (!customPresets || customPresets.length === 0) {
      toast.info("No custom chips to export yet.");
      return;
    }
    const escapeCsv = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      // Quote fields with commas, quotes, or newlines; escape " → ""
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const nowMs = Date.now();
    const DAY_MS = 24 * 60 * 60 * 1000;
    const totalPicks = customPresets.reduce(
      (sum, p) => sum + (p.pick_count || 0), 0,
    );
    const header = [
      "label", "pick_count", "share_pct", "last_7d_picks",
      "last_picked_at", "days_since_last_pick",
      "created_at", "created_by",
    ];
    const rows = customPresets.map((p) => {
      const trendKey = p.label_lower || p.label.toLowerCase();
      const trend = presetTrends[trendKey] || [];
      const last7 = trend.slice(-7).reduce((a, b) => a + b, 0);
      const share = totalPicks > 0
        ? Math.round(((p.pick_count || 0) / totalPicks) * 1000) / 10
        : 0;
      let daysSince = "";
      const lastAtMs = Date.parse(p.last_picked_at || "");
      if (!Number.isNaN(lastAtMs)) {
        daysSince = Math.floor((nowMs - lastAtMs) / DAY_MS);
      }
      return [
        p.label,
        p.pick_count || 0,
        share,
        last7,
        p.last_picked_at || "",
        daysSince,
        p.created_at || "",
        p.created_by || "",
      ].map(escapeCsv).join(",");
    });
    const csv = [header.join(","), ...rows].join("\n") + "\n";
    // Trigger a download; use a blob URL so we can add a meaningful
    // filename with today's date for easy retrieval later.
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `preset-analytics-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(
      `Downloaded ${rows.length} chip${rows.length !== 1 ? "s" : ""} · preset-analytics-${today}.csv`,
    );
  };
  const bulkArchiveStale = async () => {
    const labels = (presetAnalytics.stale || []).map((p) => p.label);
    if (labels.length === 0) return;
    setBulkArchivingStale(true);
    setBulkArchiveConfirming(false);
    try {
      const { data } = await client.post(
        "/admin/leaderboard-digest/skip-reason-presets/bulk-archive",
        { labels },
      );
      const archivedLabels = data.archived || [];
      toast.success(
        `Archived ${archivedLabels.length} stale chip${archivedLabels.length !== 1 ? "s" : ""}.`,
        {
          duration: 5000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await client.post(
                  "/admin/leaderboard-digest/skip-reason-presets/bulk-unarchive",
                  { labels: archivedLabels },
                );
                toast.success(`Restored ${archivedLabels.length} chip${archivedLabels.length !== 1 ? "s" : ""}.`);
                loadSkipSettings();
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail || "Failed to undo bulk archive",
                );
              }
            },
          },
        },
      );
      // Refresh the live list + archived list so the analytics panel
      // reflects the new state right away.
      loadSkipSettings();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Bulk archive failed",
      );
    } finally {
      setBulkArchivingStale(false);
    }
  };

  const toggleSkipNext = async () => {
    // Setting the skip requires a reason; clearing does not.
    const isSetting = skipSettings?.skip_next_iso_week !== skipSettings?.next_sunday_iso_week;
    if (isSetting && !skipReasonOpen) {
      setSkipReasonOpen(true);
      return;
    }
    setSkipSaving(true);
    try {
      const payload = isSetting ? { reason: skipReason.trim() } : {};
      const { data } = await client.post(
        "/admin/leaderboard-digest/skip-next", payload,
      );
      if (data.action === "set") {
        toast.success(
          `Sunday ${data.skip_next_iso_week} skipped · reason: "${data.skip_reason}"`,
        );
      } else {
        toast.info(
          `Sunday ${data.next_sunday_iso_week} digest is back on schedule.`,
        );
      }
      setSkipReasonOpen(false);
      setSkipReason("");
      await loadSkipSettings();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to toggle skip",
      );
    } finally {
      setSkipSaving(false);
    }
  };

  const regenerateWithMockNames = async () => {
    setSlackPreviewLoading(true);
    try {
      const { data } = await client.post(
        "/admin/leaderboard-digest/slack-test-fire/preview",
        { top_names: mockNames },
      );
      setSlackPreview(data);
      setPreviewJsonCopied(false);
      toast.success("Preview regenerated with your fleet names.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to regenerate preview",
      );
    } finally {
      setSlackPreviewLoading(false);
    }
  };

  const milestoneBadge = (m) => {
    if (m === 100) return { text: "100+", color: "emerald" };
    if (m === 25) return { text: "25+", color: "violet" };
    if (m === 5) return { text: "5+", color: "amber" };
    return null;
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="admin-sharing-leaderboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="pill pill-info mb-2 inline-flex items-center gap-1.5">
            <Trophy size={11} weight="fill" /> ADMIN · LEADERBOARD
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Rate Card Sharing Leaderboard
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
            Fleets ranked by total rate cards shared with shippers. Downloads + successful emails, most-recent first at the top.
          </p>
        </div>
        <button
          onClick={() => load(window)}
          disabled={loading}
          data-testid="admin-sharing-leaderboard-refresh"
          className="btn-secondary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 shrink-0"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Sunday Skip Toggle */}
      {skipSettings && (
        <div
          className="mb-4 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
          data-testid="admin-sharing-leaderboard-skip-card"
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {skipSettings.skip_next_iso_week === skipSettings.next_sunday_iso_week ? (
              <CalendarSlash size={20} weight="duotone" className="text-amber-400 shrink-0 mt-0.5" />
            ) : (
              <CalendarCheck size={20} weight="duotone" className="text-emerald-400 shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <div className="text-white text-sm font-semibold" data-testid="admin-sharing-leaderboard-skip-status">
                {skipSettings.skip_next_iso_week === skipSettings.next_sunday_iso_week
                  ? `Next Sunday's digest (${skipSettings.skip_next_iso_week}) is SET TO SKIP.`
                  : `Next Sunday's digest (${skipSettings.next_sunday_iso_week}) is scheduled to fire.`}
              </div>
              <div className="text-[12px] text-neutral-400 mt-0.5">
                {skipSettings.skip_next_iso_week === skipSettings.next_sunday_iso_week ? (
                  <>
                    Set by <span className="text-neutral-300">{skipSettings.skip_set_by || "admin"}</span>.
                    {skipSettings.skip_reason && (
                      <> Reason: <span className="text-neutral-300 italic" data-testid="admin-sharing-leaderboard-skip-reason-display">"{skipSettings.skip_reason}"</span>.</>
                    )}
                    {" "}Cleared automatically after Sunday.
                  </>
                ) : (
                  <>Toggle skip if you're mid-migration and don't want a real digest firing this week.</>
                )}
              </div>
            </div>
          </div>
          {skipReasonOpen && skipSettings.skip_next_iso_week !== skipSettings.next_sunday_iso_week && (
            <div
              className="w-full sm:w-auto flex flex-col gap-2"
              data-testid="admin-sharing-leaderboard-skip-reason-form"
            >
              {/* Quick-pick presets — cover 90% of ops' skip reasons so
                  they can chip-and-go without typing. Clicking a chip
                  auto-fills the input; ops can still edit or type
                  something bespoke afterwards. Built-ins are hardcoded;
                  custom chips (registered via `+ Add chip`) persist
                  server-side and are visible to the whole team. */}
              <div
                className="flex flex-wrap gap-1.5 items-center"
                data-testid="admin-sharing-leaderboard-skip-reason-presets"
              >
                <span className="text-[10px] uppercase tracking-widest text-neutral-500 self-center mr-1">
                  Quick pick:
                </span>
                {BUILT_IN_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setSkipReason(preset)}
                    data-testid={`admin-sharing-leaderboard-skip-reason-preset-${preset.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                    className={`text-[10px] rounded-full px-2.5 py-1 border transition-colors ${
                      skipReason === preset
                        ? "bg-amber-500/20 border-amber-500/40 text-amber-200 font-semibold"
                        : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
                    }`}
                  >
                    {preset}
                  </button>
                ))}
                {customPresets.map((p) => {
                  const isActive = skipReason === p.label;
                  const pickCount = p.pick_count || 0;
                  return (
                    <span
                      key={p.label}
                      data-testid={`admin-sharing-leaderboard-skip-reason-custom-preset-${(p.label_lower || p.label.toLowerCase()).replace(/[^a-z0-9]/g, "-")}`}
                      className={`inline-flex items-center rounded-full border transition-colors group ${
                        isActive
                          ? "bg-[#FF5A1F]/20 border-[#FF5A1F]/40 text-[#FF5A1F] font-semibold"
                          : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
                      }`}
                      title={
                        p.created_by
                          ? `Added by ${p.created_by}${pickCount > 0 ? ` · picked ${pickCount} time${pickCount !== 1 ? "s" : ""}` : " · never picked"}`
                          : "Custom preset"
                      }
                    >
                      <button
                        type="button"
                        onClick={() => pickCustomPreset(p.label)}
                        className="text-[10px] pl-2.5 pr-1 py-1"
                      >
                        {p.label}
                        {pickCount > 0 && (
                          <span
                            data-testid={`admin-sharing-leaderboard-skip-reason-custom-preset-count-${(p.label_lower || p.label.toLowerCase()).replace(/[^a-z0-9]/g, "-")}`}
                            className={`ml-1 tabular-nums font-mono ${
                              isActive ? "text-[#FF5A1F]/70" : "text-neutral-600"
                            }`}
                          >
                            ×{pickCount}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeCustomPreset(p.label)}
                        data-testid={`admin-sharing-leaderboard-skip-reason-custom-preset-remove-${(p.label_lower || p.label.toLowerCase()).replace(/[^a-z0-9]/g, "-")}`}
                        className="pl-0.5 pr-1.5 py-1 text-neutral-500 hover:text-rose-300 transition-colors"
                        aria-label={`Remove ${p.label} preset`}
                        title={`Remove "${p.label}"`}
                      >
                        <X size={9} weight="bold" />
                      </button>
                    </span>
                  );
                })}
                {/* + Add chip — inline expander so the whole flow stays
                    on one row. Enter to save, X to cancel. */}
                {!presetAdderOpen ? (
                  <button
                    type="button"
                    onClick={() => setPresetAdderOpen(true)}
                    data-testid="admin-sharing-leaderboard-skip-reason-preset-add-btn"
                    className="inline-flex items-center gap-1 text-[10px] rounded-full px-2.5 py-1 border border-dashed border-white/15 text-neutral-500 hover:text-white hover:border-white/40 transition-colors"
                    title="Save a custom skip reason chip for the whole team"
                  >
                    <Plus size={9} weight="bold" />
                    Add chip
                  </button>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/[0.04] pl-2 pr-1 py-0.5"
                    data-testid="admin-sharing-leaderboard-skip-reason-preset-adder"
                  >
                    <input
                      type="text"
                      autoFocus
                      value={newPresetLabel}
                      onChange={(e) => setNewPresetLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newPresetLabel.trim()) {
                          e.preventDefault();
                          addCustomPreset();
                        } else if (e.key === "Escape") {
                          setPresetAdderOpen(false);
                          setNewPresetLabel("");
                        }
                      }}
                      placeholder="New preset…"
                      maxLength={40}
                      data-testid="admin-sharing-leaderboard-skip-reason-preset-input"
                      className="bg-transparent text-[10px] text-white w-28 focus:outline-none placeholder:text-neutral-500"
                    />
                    <button
                      type="button"
                      onClick={addCustomPreset}
                      disabled={presetSaving || !newPresetLabel.trim()}
                      data-testid="admin-sharing-leaderboard-skip-reason-preset-save-btn"
                      className="text-[10px] rounded-full px-2 py-0.5 bg-[#FF5A1F] hover:bg-[#FF5A1F]/90 disabled:opacity-50 text-white font-semibold"
                    >
                      {presetSaving ? "…" : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPresetAdderOpen(false); setNewPresetLabel(""); }}
                      className="text-neutral-400 hover:text-white p-0.5"
                      aria-label="Cancel add preset"
                    >
                      <X size={10} weight="bold" />
                    </button>
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <input
                  type="text"
                  autoFocus
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && skipReason.trim()) toggleSkipNext(); }}
                  placeholder="Why skip? (holiday · testing · migration)"
                  maxLength={200}
                  data-testid="admin-sharing-leaderboard-skip-reason-input"
                  className="w-full sm:w-72 bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-xs text-white placeholder:text-neutral-500 focus:outline-none focus:border-amber-500/40"
                />
                <button
                  onClick={() => { setSkipReasonOpen(false); setSkipReason(""); }}
                  data-testid="admin-sharing-leaderboard-skip-reason-cancel"
                  className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-300 hover:text-white hover:border-white/30 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={toggleSkipNext}
                  disabled={skipSaving || !skipReason.trim()}
                  data-testid="admin-sharing-leaderboard-skip-confirm-btn"
                  className="rounded-full px-4 py-2 text-xs font-semibold bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 disabled:opacity-50 transition-colors"
                >
                  {skipSaving ? "Saving…" : "Confirm skip"}
                </button>
              </div>
            </div>
          )}
          {!skipReasonOpen && (
            <button
              onClick={toggleSkipNext}
              disabled={skipSaving}
              data-testid="admin-sharing-leaderboard-skip-toggle-btn"
              className={`rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0 transition-colors ${
                skipSettings.skip_next_iso_week === skipSettings.next_sunday_iso_week
                  ? "border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  : "border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              }`}
            >
              {skipSaving ? "Saving…" : (
                skipSettings.skip_next_iso_week === skipSettings.next_sunday_iso_week
                  ? "Unskip Sunday"
                  : "Skip next Sunday"
              )}
            </button>
          )}
        </div>
      )}

      {/* Preset Analytics — collapsible card. Only renders when there
          are custom chips to analyze (built-ins are permanent so we
          don't rank them here). Surfaces top 5 by picks + a stale
          chip hint so ops can prune chips that no one uses. */}
      {presetAnalytics.hasData && (
        <div
          className="mb-4 rounded-lg border border-white/[0.04] bg-white/[0.02] overflow-hidden"
          data-testid="admin-sharing-leaderboard-preset-analytics-card"
        >
          <button
            type="button"
            onClick={() => setPresetAnalyticsOpen((v) => !v)}
            data-testid="admin-sharing-leaderboard-preset-analytics-toggle"
            aria-expanded={presetAnalyticsOpen}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors text-left"
          >
            <div className="inline-flex items-center gap-2 min-w-0">
              <ChartBar size={14} weight="bold" className="text-sky-400 shrink-0" />
              <span className="text-[11px] uppercase tracking-widest text-neutral-400">
                Preset Analytics
              </span>
              <span className="text-[10px] text-neutral-500 truncate">
                {presetAnalytics.totalPicks} pick{presetAnalytics.totalPicks !== 1 ? "s" : ""} across {customPresets.length} custom chip{customPresets.length !== 1 ? "s" : ""}
                {presetAnalytics.stale.length > 0 && (
                  <span
                    className="ml-2 inline-flex items-center gap-0.5 text-amber-300"
                    data-testid="admin-sharing-leaderboard-preset-analytics-stale-badge"
                  >
                    <Warning size={9} weight="fill" />
                    {presetAnalytics.stale.length} stale
                  </span>
                )}
                {archivedPresets.length > 0 && (
                  <span
                    className="ml-2 text-neutral-400"
                    data-testid="admin-sharing-leaderboard-preset-analytics-archived-badge"
                  >
                    · {archivedPresets.length} archived
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Sparkline timespan picker — only surfaces when the
                  analytics panel is open (otherwise it's decorative
                  noise). Renders inside the toggle button but its
                  onClick handlers stop propagation so clicking a
                  segment doesn't accidentally collapse the panel. */}
              {presetAnalyticsOpen && (
                <div
                  className="flex items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.02] p-0.5"
                  data-testid="admin-sharing-leaderboard-preset-analytics-timespan-picker"
                  onClick={(e) => e.stopPropagation()}
                >
                  {SPARKLINE_TIMESPANS.map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSparklineDays(days); }}
                      data-testid={`admin-sharing-leaderboard-preset-analytics-timespan-${days}d`}
                      aria-pressed={sparklineDays === days}
                      className={`text-[9px] font-mono w-8 h-5 rounded-full inline-flex items-center justify-center transition-colors ${
                        sparklineDays === days
                          ? "bg-[#FF5A1F] text-white font-bold"
                          : "text-neutral-500 hover:text-white"
                      }`}
                      title={`Sparkline window: last ${days} days`}
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              )}
              {presetAnalyticsOpen
                ? <CaretDown size={11} weight="bold" className="text-neutral-500" />
                : <CaretRight size={11} weight="bold" className="text-neutral-500" />}
            </div>
          </button>
          {presetAnalyticsOpen && (
            <div
              className="px-4 py-3 border-t border-white/[0.04] grid grid-cols-1 lg:grid-cols-2 gap-4"
              data-testid="admin-sharing-leaderboard-preset-analytics-body"
            >
              {/* Sparkline legend — spans both columns above the two
                  panes so ops immediately maps colours to meaning
                  when the sparkline first shows. Only useful when
                  there's at least one hot chip to show. */}
              {presetAnalytics.top5.length > 0 && (
                <div
                  className="lg:col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-neutral-500 -mt-1"
                  data-testid="admin-sharing-leaderboard-preset-analytics-sparkline-legend"
                >
                  <span className="uppercase tracking-widest text-neutral-600 mr-1">
                    Sparkline ({sparklineDays}d):
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-[#FF5A1F]" />
                    last 7 days
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-sky-400" />
                    older
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm bg-neutral-600 opacity-70" />
                    no picks
                  </span>
                  {/* CSV export — pushed to the right so the legend
                      keeps its natural left-aligned layout. */}
                  <button
                    type="button"
                    onClick={exportAnalyticsCsv}
                    data-testid="admin-sharing-leaderboard-preset-analytics-csv-export-btn"
                    className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] border border-white/10 text-neutral-400 hover:text-white hover:border-white/30 hover:bg-white/[0.04] transition-colors"
                    title="Download all custom chip analytics as CSV"
                  >
                    <DownloadSimple size={10} weight="bold" />
                    Export CSV
                  </button>
                </div>
              )}
              {/* Top-5 hot chips */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                  Top {Math.min(5, presetAnalytics.top5.length)} by picks
                </div>
                {presetAnalytics.top5.length === 0 ? (
                  <div
                    className="text-xs text-neutral-500 italic"
                    data-testid="admin-sharing-leaderboard-preset-analytics-top5-empty"
                  >
                    No chip has been picked yet — every custom chip is
                    at zero. Try one on a real skip to seed the data.
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {presetAnalytics.top5.map((p, idx) => {
                      const share = presetAnalytics.totalPicks > 0
                        ? Math.round((p.pick_count / presetAnalytics.totalPicks) * 100)
                        : 0;
                      const trendKey = p.label_lower || p.label.toLowerCase();
                      const trend = presetTrends[trendKey] || [];
                      const trendMax = Math.max(1, ...trend);
                      // Compute the date for each bar: bar[0] is the
                      // oldest day (`sparklineDays - 1` days ago), bar
                      // [sparklineDays - 1] is today. Used for
                      // hover-tooltips.
                      const oldestDate = new Date();
                      oldestDate.setDate(oldestDate.getDate() - (sparklineDays - 1));
                      oldestDate.setHours(0, 0, 0, 0);
                      const barDate = (i) => {
                        const d = new Date(oldestDate);
                        d.setDate(d.getDate() + i);
                        return d.toLocaleDateString(undefined, {
                          month: "short", day: "numeric",
                        });
                      };
                      // Bar geometry scales with timespan so 7/30/90
                      // days all fit inside the same ~90px SVG width.
                      // `barSlot` = each bar's horizontal slot; leave
                      // a tiny gap so bars stay visually distinct.
                      const barSlot = 90 / sparklineDays;
                      const barWidth = Math.max(1, barSlot - 0.3);
                      const last7Sum = trend
                        .slice(-7)
                        .reduce((a, b) => a + b, 0);
                      const rowKey = trendKey.replace(/[^a-z0-9]/g, "-");
                      return (
                        <li
                          key={p.label}
                          data-testid={`admin-sharing-leaderboard-preset-analytics-top5-row-${rowKey}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="w-4 text-right text-neutral-600 font-mono tabular-nums shrink-0">
                            {idx + 1}
                          </span>
                          <span className="text-white truncate min-w-0 flex-1">
                            {p.label}
                          </span>
                          {/* 30-day pick velocity sparkline — 30 tiny
                              bars, each height ∝ count / trendMax.
                              Each bar has an inline `<title>` for
                              native hover-tooltips (accessibility +
                              broad browser support). */}
                          <svg
                            data-testid={`admin-sharing-leaderboard-preset-analytics-sparkline-${rowKey}`}
                            width="90"
                            height="14"
                            viewBox="0 0 90 14"
                            className="shrink-0"
                            aria-label={
                              trend.length
                                ? `${sparklineDays}-day pick velocity for ${p.label}`
                                : "No pick data yet"
                            }
                          >
                            {Array.from({ length: sparklineDays }).map((_, i) => {
                              const raw = trend[i] || 0;
                              const h = trend.length > 0
                                ? Math.max(1, Math.round((raw / trendMax) * 12))
                                : 1;
                              // Last 7 days are always the trailing 7
                              // buckets regardless of timespan — the
                              // "hot" colour keeps consistent meaning
                              // across the 7/30/90 views.
                              const isLast7 = i >= sparklineDays - 7;
                              const tooltip = raw === 0
                                ? `${barDate(i)} · no picks`
                                : `${barDate(i)} · ${raw} pick${raw !== 1 ? "s" : ""}`;
                              return (
                                <rect
                                  key={i}
                                  x={i * barSlot}
                                  y={14 - h}
                                  width={barWidth}
                                  height={h}
                                  fill={
                                    raw === 0
                                      ? "#3f3f46"
                                      : isLast7
                                        ? "#FF5A1F"
                                        : "#38bdf8"
                                  }
                                  opacity={raw === 0 ? 0.5 : 1}
                                  data-testid={`admin-sharing-leaderboard-preset-analytics-sparkline-bar-${rowKey}-${i}`}
                                  className="hover:opacity-100"
                                >
                                  <title>{tooltip}</title>
                                </rect>
                              );
                            })}
                          </svg>
                          {last7Sum > 0 && (
                            <span
                              className="text-[9px] rounded-full px-1.5 py-0.5 bg-[#FF5A1F]/15 text-[#FF5A1F] font-semibold shrink-0"
                              data-testid={`admin-sharing-leaderboard-preset-analytics-hot-badge-${rowKey}`}
                              title={`${last7Sum} pick${last7Sum !== 1 ? "s" : ""} in last 7 days`}
                            >
                              HOT
                            </span>
                          )}
                          <span className="w-14 text-right text-neutral-400 font-mono tabular-nums shrink-0 text-[10px]">
                            {p.pick_count} · {share}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {/* Stale chips */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1">
                    <Warning size={10} weight="fill" className="text-amber-400" />
                    Stale ({presetAnalytics.stale.length})
                  </div>
                  {/* Bulk archive shortcut — appears only when there's
                      more than one stale chip. Two-step confirm to
                      prevent accidental mass-archive on large lists. */}
                  {presetAnalytics.stale.length > 1 && (
                    bulkArchiveConfirming ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/[0.08] pl-2 pr-0.5 py-0.5"
                        data-testid="admin-sharing-leaderboard-preset-analytics-bulk-archive-confirm"
                      >
                        <span className="text-[9px] uppercase tracking-widest text-amber-200">
                          Sure? Archive {presetAnalytics.stale.length}
                        </span>
                        <button
                          type="button"
                          onClick={bulkArchiveStale}
                          disabled={bulkArchivingStale}
                          data-testid="admin-sharing-leaderboard-preset-analytics-bulk-archive-confirm-yes"
                          className="text-[9px] rounded-full px-2 py-0.5 bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-50 transition-colors"
                        >
                          {bulkArchivingStale ? "…" : "Yes"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setBulkArchiveConfirming(false)}
                          data-testid="admin-sharing-leaderboard-preset-analytics-bulk-archive-cancel"
                          className="text-neutral-400 hover:text-white p-0.5"
                          aria-label="Cancel bulk archive"
                        >
                          <X size={9} weight="bold" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setBulkArchiveConfirming(true)}
                        data-testid="admin-sharing-leaderboard-preset-analytics-bulk-archive-btn"
                        className="text-[9px] rounded-full px-2 py-0.5 border border-amber-500/30 text-amber-300 hover:bg-amber-500/[0.08] hover:border-amber-500/50 inline-flex items-center gap-1 transition-colors"
                        title={`Archive all ${presetAnalytics.stale.length} stale chips in one shot`}
                      >
                        Archive all {presetAnalytics.stale.length} →
                      </button>
                    )
                  )}
                </div>
                {presetAnalytics.stale.length === 0 ? (
                  <div
                    className="text-xs text-neutral-500 italic"
                    data-testid="admin-sharing-leaderboard-preset-analytics-stale-empty"
                  >
                    Every chip is fresh — either it's been picked
                    recently or it's less than 30 days old.
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {presetAnalytics.stale.map((p) => {
                      const never = (p.pick_count || 0) === 0;
                      return (
                        <li
                          key={p.label}
                          data-testid={`admin-sharing-leaderboard-preset-analytics-stale-row-${(p.label_lower || p.label.toLowerCase()).replace(/[^a-z0-9]/g, "-")}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="text-neutral-300 truncate min-w-0 flex-1">
                            {p.label}
                          </span>
                          <span
                            className="text-[10px] text-amber-300 italic shrink-0"
                            title={
                              never
                                ? `Never picked; created ${p.created_at ? p.created_at.slice(0, 10) : "unknown"}`
                                : `Last picked ${p.last_picked_at ? p.last_picked_at.slice(0, 10) : "unknown"}`
                            }
                          >
                            {never ? "never picked" : `last ${(p.last_picked_at || "").slice(0, 10)}`}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeCustomPreset(p.label)}
                            data-testid={`admin-sharing-leaderboard-preset-analytics-stale-remove-${(p.label_lower || p.label.toLowerCase()).replace(/[^a-z0-9]/g, "-")}`}
                            className="rounded-full p-1 text-neutral-500 hover:text-rose-300 hover:bg-white/[0.04] transition-colors shrink-0"
                            aria-label={`Remove stale preset ${p.label}`}
                            title={`Prune "${p.label}"`}
                          >
                            <Trash size={10} weight="fill" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {/* Archived — soft-deleted chips. Full-width row that
                  spans both columns above so ops can scan pruned
                  chips without opening a separate view. Restore
                  icon flips them back onto the live row. */}
              {archivedPresets.length > 0 && (
                <div
                  className="lg:col-span-2 pt-3 border-t border-white/[0.04]"
                  data-testid="admin-sharing-leaderboard-preset-analytics-archived-section"
                >
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 inline-flex items-center gap-1">
                    <Trash size={10} weight="fill" className="text-neutral-500" />
                    Archived ({archivedPresets.length})
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
                    {archivedPresets.map((p) => {
                      const key = (p.label_lower || p.label.toLowerCase()).replace(/[^a-z0-9]/g, "-");
                      return (
                        <li
                          key={p.label}
                          data-testid={`admin-sharing-leaderboard-preset-analytics-archived-row-${key}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          <span className="text-neutral-400 truncate min-w-0 flex-1 line-through decoration-neutral-600">
                            {p.label}
                          </span>
                          <span
                            className="text-[10px] text-neutral-600 italic shrink-0"
                            title={
                              p.archived_by
                                ? `Archived by ${p.archived_by} on ${(p.archived_at || "").slice(0, 10)}`
                                : "Archived"
                            }
                          >
                            {(p.archived_at || "").slice(0, 10)}
                          </span>
                          <button
                            type="button"
                            onClick={() => unarchiveCustomPreset(p.label)}
                            data-testid={`admin-sharing-leaderboard-preset-analytics-unarchive-${key}`}
                            className="rounded-full p-1 text-neutral-500 hover:text-emerald-300 hover:bg-white/[0.04] transition-colors shrink-0"
                            aria-label={`Restore archived preset ${p.label}`}
                            title={`Restore "${p.label}" onto the chip row`}
                          >
                            <ArrowUUpLeft size={10} weight="bold" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Skip History (past admin skips) */}
      {skipHistory.length > 0 && (
        <div
          className="mb-4 rounded-lg border border-white/[0.04] bg-white/[0.02] overflow-hidden"
          data-testid="admin-sharing-leaderboard-skip-history"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04]">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              Past skips ({skipHistory.length})
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.02]">
                <tr className="text-[9px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-2">Week</th>
                  <th className="text-left px-4 py-2">Set by</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-left px-4 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {skipHistory.map((s) => (
                  <tr
                    key={s.iso_week}
                    data-testid={`admin-sharing-leaderboard-skip-history-row-${s.iso_week}`}
                    className="border-t border-white/[0.04]"
                  >
                    <td className="px-4 py-2 text-white font-mono">
                      {s.iso_week}
                    </td>
                    <td className="px-4 py-2 text-neutral-300">
                      {s.skipped_by || "—"}
                    </td>
                    <td className="px-4 py-2 text-neutral-300 italic max-w-xs truncate" title={s.skip_reason}>
                      {s.skip_reason || "—"}
                    </td>
                    <td className="px-4 py-2 text-neutral-500 text-[11px]">
                      {s.fired_at ? new Date(s.fired_at).toLocaleString(undefined, {
                        month: "short", day: "numeric", year: "numeric",
                      }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Slack test-fire */}
      <div
        className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
        data-testid="admin-sharing-leaderboard-slack-test-fire-card"
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <SlackLogo size={20} weight="duotone" className="text-[#4A154B] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-white text-sm font-semibold">
              Test-fire the Sunday Slack digest
            </div>
            <div className="text-[12px] text-neutral-400 mt-0.5">
              Posts a synthetic digest to your configured Slack channel
              with a TEST FIRE banner so you can verify wiring before Sunday 12pm UTC.
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={openSlackPreview}
            data-testid="admin-sharing-leaderboard-slack-preview-btn"
            className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-200 hover:bg-white/[0.04] hover:border-white/30 inline-flex items-center gap-1.5 transition-colors"
          >
            <Eye size={12} weight="bold" />
            Preview
          </button>
          <button
            onClick={slackTestFire}
            disabled={slackTestFiring}
            data-testid="admin-sharing-leaderboard-slack-test-fire-btn"
            className="rounded-full px-4 py-2 text-xs bg-[#4A154B] hover:bg-[#4A154B]/90 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5 transition-colors"
          >
            <SlackLogo size={12} weight="fill" />
            {slackTestFiring ? "Posting…" : "Post to Slack now"}
          </button>
        </div>
      </div>

      {/* Window toggle */}
      <div className="flex gap-2 mb-6" data-testid="admin-sharing-leaderboard-window-toggle">
        {[
          { key: "all", label: "All time" },
          { key: "90", label: "Last 90 days" },
          { key: "30", label: "Last 30 days" },
        ].map((w) => (
          <button
            key={w.key}
            onClick={() => setWindow(w.key)}
            data-testid={`admin-sharing-leaderboard-window-${w.key}`}
            className={`text-xs rounded-full px-4 py-2 transition-colors border ${
              window === w.key
                ? "bg-[#FF5A1F] border-[#FF5A1F] text-white font-semibold"
                : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fleets on board</div>
          <div className="text-2xl font-mono font-bold text-white mt-1" data-testid="admin-sharing-leaderboard-total">
            {total}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Top fleet total</div>
          <div className="text-2xl font-mono font-bold text-[#FF5A1F] mt-1"
               data-testid="admin-sharing-leaderboard-top-total">
            {rows[0]?.total || 0}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Window</div>
          <div className="text-sm text-neutral-300 mt-1">
            {window === "all" ? "All time" : `Last ${window} days`}
          </div>
        </div>
      </div>

      {loading && rows.length === 0 && (
        <div className="card-surface p-8 text-center text-neutral-400">Loading…</div>
      )}

      {!loading && rows.length === 0 && (
        <div className="card-surface p-8 text-center" data-testid="admin-sharing-leaderboard-empty">
          <div className="mb-3 text-neutral-400">No shares in this window yet.</div>
          <div className="text-xs text-neutral-500">
            Try a longer window or wait for fleets to start sharing their rate cards.
          </div>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="card-surface overflow-hidden" data-testid="admin-sharing-leaderboard-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 w-12">#</th>
                  <th className="text-left px-4 py-3">Fleet</th>
                  <th className="text-right px-4 py-3">Downloads</th>
                  <th className="text-right px-4 py-3">Emails</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3">Milestone</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const m = milestoneBadge(row.milestone_reached);
                  const isTop = row.rank === 1;
                  return (
                    <tr
                      key={row.owner_id}
                      data-testid={`admin-sharing-leaderboard-row-${row.owner_id}`}
                      className={`border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors ${
                        isTop ? "bg-[#FF5A1F]/[0.03]" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        {isTop ? (
                          <div className="inline-flex items-center gap-1 text-[#FF5A1F] font-bold text-lg"
                               data-testid={`admin-sharing-leaderboard-rank-${row.owner_id}`}>
                            <Crown size={16} weight="fill" />
                            {row.rank}
                          </div>
                        ) : (
                          <div className="text-neutral-400 font-mono tabular-nums">{row.rank}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="inline-flex items-start gap-2 min-w-0">
                          <Buildings size={14} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className={`truncate ${isTop ? "text-white font-semibold" : "text-white"}`}>
                              {row.company_name || row.owner_email || "Unknown"}
                            </div>
                            <div className="text-[11px] text-neutral-500 font-mono truncate">
                              {row.owner_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1 justify-end text-emerald-300 font-mono tabular-nums">
                          <FilePdf size={11} weight="fill" />
                          {row.downloads}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1 justify-end text-violet-300 font-mono tabular-nums">
                          <EnvelopeSimple size={11} weight="fill" />
                          {row.emails}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="text-white font-bold font-mono tabular-nums text-lg">
                          {row.total}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {m ? (
                          <span
                            data-testid={`admin-sharing-leaderboard-milestone-${row.owner_id}`}
                            className={`inline-flex items-center text-[10px] font-bold rounded-full px-2 py-0.5 border ${
                              m.color === "emerald"
                                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                                : m.color === "violet"
                                ? "bg-violet-500/10 text-violet-300 border-violet-500/40"
                                : "bg-amber-500/10 text-amber-300 border-amber-500/40"
                            }`}
                          >
                            {m.text}
                          </span>
                        ) : (
                          <span className="text-neutral-600 text-[10px]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Slack Test-Fire Preview Modal */}
      {slackPreviewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-sharing-leaderboard-slack-preview-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeSlackPreview();
          }}
        >
          <div className="card-surface w-full max-w-3xl my-6 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-2">
                <Code size={16} weight="duotone" className="text-[#4A154B]" />
                <div>
                  <div className="text-white font-semibold text-sm">
                    Slack Block Kit preview
                  </div>
                  <div className="text-[11px] text-neutral-500">
                    Nothing is posted until you hit "Post to Slack now".
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={refreshSlackPreview}
                  disabled={slackPreviewLoading}
                  data-testid="admin-sharing-leaderboard-slack-preview-refresh"
                  className="text-neutral-400 hover:text-white disabled:opacity-50 p-1 rounded transition-colors"
                  aria-label="Refresh preview"
                  title="Regenerate mock digest"
                >
                  <ArrowClockwise
                    size={14}
                    weight="bold"
                    className={slackPreviewLoading ? "animate-spin" : ""}
                  />
                </button>
                <button
                  onClick={closeSlackPreview}
                  data-testid="admin-sharing-leaderboard-slack-preview-close"
                  className="text-neutral-400 hover:text-white p-1 rounded transition-colors"
                  aria-label="Close preview"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            </div>

            {slackPreviewLoading && (
              <div
                className="p-8 text-center text-neutral-400 text-sm"
                data-testid="admin-sharing-leaderboard-slack-preview-loading"
              >
                Loading preview…
              </div>
            )}

            {!slackPreviewLoading && slackPreview && (
              <div className="p-5 space-y-4">
                <div
                  className={`text-xs rounded-lg px-3 py-2 border ${
                    slackPreview.webhook_configured
                      ? "bg-emerald-500/[0.06] border-emerald-500/30 text-emerald-300"
                      : "bg-amber-500/[0.06] border-amber-500/30 text-amber-300"
                  }`}
                  data-testid="admin-sharing-leaderboard-slack-preview-webhook-status"
                >
                  {slackPreview.webhook_configured ? (
                    <>
                      <b>Webhook wired</b>: <code className="text-[11px] font-mono opacity-80">{slackPreview.webhook_preview}</code>
                    </>
                  ) : (
                    <>
                      <b>No webhook configured.</b> Set{" "}
                      <code className="text-[11px] font-mono">SLACK_LEADERBOARD_WEBHOOK_URL</code>{" "}
                      before posting.
                    </>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      ISO week
                    </div>
                    <button
                      onClick={() => setMockEditorOpen((v) => !v)}
                      data-testid="admin-sharing-leaderboard-slack-preview-editor-toggle"
                      className="text-[11px] rounded-full px-3 py-1 inline-flex items-center gap-1.5 transition-colors border border-white/15 text-neutral-300 hover:text-white hover:border-white/30"
                    >
                      <PencilSimple size={11} weight="bold" />
                      {mockEditorOpen ? "Hide fleet editor" : "Edit fleet names"}
                    </button>
                  </div>
                  <div className="text-white font-mono text-sm">
                    {slackPreview.iso_week} · TEST FIRE
                  </div>
                </div>

                {/* Mock Editor — customize the fake fleet names */}
                {mockEditorOpen && (
                  <div
                    className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2"
                    data-testid="admin-sharing-leaderboard-slack-preview-editor"
                  >
                    <div className="text-[11px] text-neutral-400">
                      Override the "Sample Fleet A/B/C" names to demo the
                      digest with a real fleet. Leave blank to keep the default.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {[0, 1, 2].map((i) => (
                        <input
                          key={i}
                          type="text"
                          list="admin-sharing-leaderboard-fleet-list"
                          value={mockNames[i]}
                          onChange={(e) => {
                            const next = [...mockNames];
                            next[i] = e.target.value;
                            setMockNames(next);
                          }}
                          placeholder={`Sample Fleet ${["A", "B", "C"][i]}`}
                          data-testid={`admin-sharing-leaderboard-slack-preview-mock-${i}`}
                          className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#FF5A1F]/40"
                        />
                      ))}
                    </div>
                    {/* Native autocomplete source — reuses the leaderboard
                        rows so the mock editor suggests real fleets AND
                        owner emails as a secondary group. */}
                    <datalist
                      id="admin-sharing-leaderboard-fleet-list"
                      data-testid="admin-sharing-leaderboard-fleet-datalist"
                    >
                      {rows.map((r) => {
                        const name = r.company_name || r.owner_email || "Unknown";
                        return (
                          <option
                            key={`co-${r.owner_id}`}
                            value={name}
                          />
                        );
                      })}
                      {rows.filter((r) => r.owner_email && r.owner_email !== r.company_name).map((r) => (
                        <option
                          key={`email-${r.owner_id}`}
                          value={r.owner_email}
                        />
                      ))}
                    </datalist>
                    <div className="flex justify-end">
                      <button
                        onClick={regenerateWithMockNames}
                        disabled={slackPreviewLoading}
                        data-testid="admin-sharing-leaderboard-slack-preview-mock-apply"
                        className="text-[11px] rounded-full px-4 py-1.5 bg-[#FF5A1F] hover:bg-[#FF5A1F]/90 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5 transition-colors"
                      >
                        <ArrowClockwise size={11} weight="bold" />
                        Apply names
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Block Kit JSON ({slackPreview.blocks?.length || 0} blocks)
                    </div>
                    <button
                      onClick={copyPreviewJson}
                      data-testid="admin-sharing-leaderboard-slack-preview-copy"
                      className={`text-[11px] rounded-full px-3 py-1 inline-flex items-center gap-1.5 transition-colors border ${
                        previewJsonCopied
                          ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                          : "border-white/15 text-neutral-300 hover:text-white hover:border-white/30"
                      }`}
                      title="Copy for Slack Block Kit Builder"
                    >
                      {previewJsonCopied ? (
                        <><Check size={11} weight="bold" /> Copied</>
                      ) : (
                        <><Copy size={11} weight="bold" /> Copy JSON</>
                      )}
                    </button>
                  </div>
                  <pre
                    className="bg-black/60 border border-white/[0.06] rounded-lg p-3 text-[11px] font-mono text-neutral-300 overflow-x-auto max-h-96 overflow-y-auto"
                    data-testid="admin-sharing-leaderboard-slack-preview-json"
                  >
                    {JSON.stringify(slackPreview.blocks, null, 2)}
                  </pre>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.04]">
                  <button
                    onClick={closeSlackPreview}
                    className="rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-200 hover:bg-white/[0.04] transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeSlackPreview();
                      slackTestFire();
                    }}
                    disabled={!slackPreview.webhook_configured}
                    data-testid="admin-sharing-leaderboard-slack-preview-post"
                    className="rounded-full px-4 py-2 text-xs bg-[#4A154B] hover:bg-[#4A154B]/90 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-1.5 transition-colors"
                  >
                    <SlackLogo size={12} weight="fill" />
                    Post this to Slack
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
